import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Sparkles, ChevronRight, Loader2, AlertTriangle, Users, User, RefreshCw } from 'lucide-react';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { DEMO_INSIGHTS } from '../lib/demo-data';
import { apiUrl, postStreamedJson } from '../lib/api';
import { RotatingStatus } from './RotatingStatus';

// The /api/ai-insights endpoint requires a valid Firebase login token (so random
// callers can't run up the Claude bill). Attach the signed-in user's token to
// every request. Works the same on web and native.
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await auth.currentUser?.getIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

interface PlayerPayload {
  id: string;
  name: string;
  number: string;
  primaryPosition: string;
  batting: any;
  pitching: any;
  recent: any;
  gameLog: any[];
  hotSignals: string[];
  coldSignals: string[];
  attributes?: Record<string, Record<string, number>> | null;
}

interface InsightsPayload {
  teamId: string;
  gameSetHash: string;
  cacheKey: string;
  teamInfo: { name: string; ageGroup: string; styleOfPlay: string };
  teamStats: any;
  teamResults?: any;   // W-L, run diff, one-run record, last-5
  teamRecent?: any;    // last-3-games team window
  teamGameArc?: any[]; // dated results, newest first
  players: PlayerPayload[];
  gameCount: number;
  isDemoSession?: boolean;
}

interface SuggestedAttributes {
  hitting?: { contact?: number; power?: number; eye?: number };
  pitching?: { velocity?: number; control?: number };
}

interface PlayerInsight {
  playerId: string;
  name: string;
  observations: string[];
  suggestion: string;
  suggestedAttributes?: SuggestedAttributes;
}

interface InsightsResult {
  teamInsight: { observations: string[]; suggestion: string };
  playerInsights: PlayerInsight[];
  coachNotes?: Record<string, string>;
  generatedAt?: string;   // ISO — lets the next report find its predecessor
  gameCount?: number;
}

/** Most recent prior report (different game set) — feeds report-to-report continuity. */
async function fetchPreviousReport(teamId: string, currentHash: string) {
  try {
    const snap = await getDocs(collection(db, `teams/${teamId}/insights`));
    const prior = snap.docs
      .filter(d => d.id !== currentHash)
      .map(d => d.data() as InsightsResult)
      .filter(d => d.generatedAt && d.teamInsight?.observations?.length)
      .sort((a, b) => (b.generatedAt || '').localeCompare(a.generatedAt || ''))[0];
    if (!prior) return null;
    return {
      generatedAt: prior.generatedAt,
      gameCount: prior.gameCount ?? null,
      observations: prior.teamInsight.observations,
      suggestion: prior.teamInsight.suggestion || null,
    };
  } catch {
    return null; // rules or network — memory silently degrades
  }
}

interface CoachsEyeModalProps {
  payload: InsightsPayload;
  onClose: () => void;
  onApplyAttributes?: (playerId: string, merged: Record<string, Record<string, number>>) => Promise<void>;
}

const LS_PREFIX = 'dugout_coachseye_v4_';
const MAX_NOTE_LENGTH = 400;

function lsLoad(key: string): InsightsResult | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch { return null; }
}

function lsSave(key: string, data: InsightsResult) {
  try { localStorage.setItem(LS_PREFIX + key, JSON.stringify(data)); } catch { /* quota */ }
}

function isValid(d: any): d is InsightsResult {
  return d?.teamInsight != null && Array.isArray(d?.playerInsights) && d.playerInsights.length > 0;
}

export const CoachsEyeModal = ({ payload, onClose, onApplyAttributes }: CoachsEyeModalProps) => {
  const [insights, setInsights] = useState<InsightsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Coach notes state: keyed by playerId, tracks current typed values
  const [coachNotes, setCoachNotes] = useState<Record<string, string>>({});
  // What was last saved/loaded — used to detect unsaved changes
  const [savedNotes, setSavedNotes] = useState<Record<string, string>>({});
  const [refining, setRefining] = useState(false);

  // Coaching Mirror: track applied/undo state per player
  const [appliedPlayers, setAppliedPlayers] = useState<Set<string>>(new Set());
  const [applyingPlayer, setApplyingPlayer] = useState<string | null>(null);

  // True once a coach has submitted notes and gotten back a refined analysis
  const isRefined = !!(insights?.coachNotes && Object.values(insights.coachNotes).some(n => n?.trim()));

  const hasUnsavedNotes = !isRefined && Object.entries(coachNotes).some(
    ([id, note]) => note.trim() && note.trim() !== (savedNotes[id] ?? '').trim()
  );

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function load() {
      // 0. Demo sessions — return hardcoded response immediately, no API call.
      if (payload.isDemoSession) {
        if (!cancelled) {
          setInsights(DEMO_INSIGHTS as InsightsResult);
          setLoading(false);
        }
        return;
      }

      // 0.5. No completed games — nothing to analyze, so never call the API.
      // (Also guards a caching hole: with zero games the gameSetHash is an
      // empty string, which is an invalid Firestore doc id — a 0-game report
      // could be generated but never cached, re-billing on every open.)
      if (!payload.gameCount) {
        if (!cancelled) setLoading(false);
        return;
      }

      // 1. localStorage (instant)
      const lsCached = lsLoad(payload.cacheKey);
      if (lsCached) {
        if (!cancelled) {
          setInsights(lsCached);
          const notes = lsCached.coachNotes ?? {};
          setCoachNotes(notes);
          setSavedNotes(notes);
          setLoading(false);
        }
        return;
      }

      // 2. Firestore (free)
      try {
        const ref = doc(db, `teams/${payload.teamId}/insights/${payload.gameSetHash}`);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const stored = snap.data() as InsightsResult;
          if (isValid(stored)) {
            lsSave(payload.cacheKey, stored);
            if (!cancelled) {
              setInsights(stored);
              const notes = stored.coachNotes ?? {};
              setCoachNotes(notes);
              setSavedNotes(notes);
              setLoading(false);
            }
            return;
          }
        }
      } catch { /* fall through */ }

      // 3. Claude API
      await callAPI(payload, {}, cancelled, setInsights, setCoachNotes, setSavedNotes, setError, setLoading);
    }

    load();
    return () => { cancelled = true; };
  }, [payload.cacheKey]);

  // ── Regenerate with coach notes ───────────────────────────────────────────
  async function handleRegenerate() {
    if (!hasUnsavedNotes || refining) return;
    setRefining(true);
    setError(null);
    const { cacheKey, teamId, gameSetHash, ...apiPayload } = payload;
    try {
      const previousReport = await fetchPreviousReport(teamId, gameSetHash);
      const data = await postStreamedJson(
        apiUrl('/api/ai-insights'),
        await authHeaders(),
        JSON.stringify({ ...apiPayload, coachNotes, previousReport }),
        'SmartScout',
      );
      if (data?.__httpError) throw new Error(data.error || 'Request failed');
      if (!isValid(data)) throw new Error('Incomplete response from server');

      const withNotes: InsightsResult = { ...data, generatedAt: new Date().toISOString(), gameCount: payload.gameCount, coachNotes };
      try {
        await setDoc(doc(db, `teams/${teamId}/insights/${gameSetHash}`), withNotes);
      } catch { /* still show result */ }
      lsSave(cacheKey, withNotes);
      setInsights(withNotes);
      setSavedNotes({ ...coachNotes });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRefining(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[100] bg-surface flex flex-col"
    >
      {/* Header — `shrink-0` keeps it from compressing as the body grows.
          Previously had `sticky top-0` but the header is already a non-
          scrolling flex row (the scrollable body is its sibling with
          `flex-1 overflow-y-auto`), so sticky was doing nothing useful and
          combined badly with `backdrop-blur` in Safari, where the header
          would scroll with the content instead of staying pinned. */}
      <header
        className="shrink-0 px-6 pb-6 border-b border-hairline flex items-center justify-between bg-surface"
        style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top))' }}
      >
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-gold-soft rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-gold-ink" />
          </div>
          <div>
            <h2 className="text-xl font-black italic uppercase">Smart Scout</h2>
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
              {payload.gameCount} game{payload.gameCount !== 1 ? 's' : ''} analyzed
            </p>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <Loader2 className="w-8 h-8 text-gold-ink animate-spin" />
            {/* Smart Scout's Claude call runs ~15-30s — fill the wait with the
                rotating dugout messages instead of one static line. */}
            <RotatingStatus />
          </div>
        )}

        {error && (
          <div className="bg-[#FBF0F0] border border-hairline p-5 rounded-2xl flex items-start space-x-3">
            <AlertTriangle className="w-4 h-4 text-danger mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] font-semibold text-danger uppercase tracking-widest mb-1">Something went wrong</p>
              <p className="text-xs text-ink-2">{error}</p>
            </div>
          </div>
        )}

        {/* Pre-wired empty state — no completed games means no API call was made. */}
        {!loading && !error && !insights && !payload.gameCount && (
          <div className="flex flex-col items-center justify-center py-24 px-8 text-center space-y-4">
            <div className="w-14 h-14 bg-gold-soft rounded-2xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-gold-ink" />
            </div>
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Scout is ready when you are</p>
            <p className="text-sm text-ink-2 leading-relaxed max-w-xs">
              To use Scout, upload stats from your most recent game.
            </p>
          </div>
        )}

        {insights && (
          <>
            {/* Team Insight */}
            <section className="bg-canvas rounded-3xl border border-hairline overflow-hidden">
              <div className="px-5 pt-5 pb-4 border-b border-hairline flex items-center space-x-3">
                <div className="w-8 h-8 bg-gold-soft rounded-xl flex items-center justify-center">
                  <Users className="w-4 h-4 text-gold-ink" />
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest">Team Overview</h3>
                  <p className="text-[9px] text-ink-2 font-semibold uppercase tracking-widest">{payload.teamInfo.name}</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="space-y-3">
                  {(insights.teamInsight.observations ?? []).map((obs, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                      <p className="text-sm text-ink-2 leading-relaxed">{obs}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-gold-soft border border-amber-500/10 rounded-2xl p-4">
                  <p className="text-[9px] font-semibold text-gold-ink uppercase tracking-widest mb-2">Coaching Focus</p>
                  <p className="text-sm text-ink-2 leading-relaxed">{insights.teamInsight.suggestion}</p>
                </div>
              </div>
            </section>

            {/* Player Insights */}
            {(insights.playerInsights ?? []).length > 0 && (
              <section className="space-y-4">
                <h3 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest px-1">Individual Spotlights</h3>
                {(insights.playerInsights ?? []).map((pi) => {
                  const noteKey = pi.playerId ?? pi.name;
                  const note = coachNotes[noteKey] ?? '';
                  const savedNote = savedNotes[noteKey] ?? '';
                  const changed = note.trim() && note.trim() !== savedNote.trim();
                  return (
                    <div key={noteKey} className="bg-canvas rounded-2xl border border-hairline overflow-hidden">
                      {/* Player header */}
                      <div className="px-5 pt-4 pb-3 border-b border-hairline flex items-center space-x-3">
                        <div className="w-7 h-7 bg-raised rounded-lg flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-ink-2" />
                        </div>
                        <p className="text-xs font-semibold uppercase tracking-wide">{pi.name}</p>
                      </div>

                      {/* AI observations */}
                      <div className="p-5 space-y-3">
                        <div className="space-y-2">
                          {(pi.observations ?? []).map((obs, i) => (
                            <div key={i} className="flex items-start space-x-3">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ink-mute shrink-0" />
                              <p className="text-xs text-ink-2 leading-relaxed">{obs}</p>
                            </div>
                          ))}
                        </div>
                        {pi.suggestion && (
                          <div className="bg-raised rounded-xl p-3">
                            <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Suggestion</p>
                            <p className="text-xs text-ink-2 leading-relaxed">{pi.suggestion}</p>
                          </div>
                        )}

                        {/* Coach note — editable before refinement, read-only after */}
                        {isRefined ? (
                          note.trim() ? (
                            <div className="pt-1">
                              <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest mb-1.5">Coach's Note</p>
                              <p className="text-xs text-ink-2 italic leading-relaxed">{note}</p>
                            </div>
                          ) : null
                        ) : (
                          <div className="pt-1 space-y-1.5">
                            <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">
                              What do you see, coach?
                            </p>
                            <textarea
                              value={note}
                              onChange={e => {
                                if (e.target.value.length <= MAX_NOTE_LENGTH) {
                                  setCoachNotes(prev => ({ ...prev, [noteKey]: e.target.value }));
                                }
                              }}
                              placeholder="Add context only you would know: mechanics, attitude, off-field situation, what you've been working on…"
                              rows={2}
                              className="w-full bg-raised border border-ink-mute rounded-xl px-3 py-2.5 text-xs text-ink-2 placeholder-ink-2 resize-none focus:outline-none focus:border-hairline transition-colors"
                            />
                            <div className="flex items-center justify-between">
                              <span className={`text-[9px] font-bold ${note.length > MAX_NOTE_LENGTH * 0.9 ? 'text-gold-ink' : 'text-ink-2'}`}>
                                {note.length}/{MAX_NOTE_LENGTH}
                              </span>
                              {changed && (
                                <span className="text-[9px] font-semibold text-gold-ink uppercase tracking-widest">unsaved</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {/* Coaching Mirror — shown when 8+ games and suggestedAttributes exist */}
            {(() => {
              const mirrorPlayers = (insights.playerInsights ?? []).filter(pi => {
                const sa = pi.suggestedAttributes;
                return sa && (
                  (sa.hitting && (sa.hitting.contact != null || sa.hitting.power != null || sa.hitting.eye != null)) ||
                  (sa.pitching && (sa.pitching.velocity != null || sa.pitching.control != null))
                );
              });
              if (mirrorPlayers.length === 0) return null;

              const playerPayloadMap = Object.fromEntries(payload.players.map(p => [p.id, p]));

              async function handleApply(pi: PlayerInsight) {
                const pp = playerPayloadMap[pi.playerId];
                if (!pp?.attributes || !pi.suggestedAttributes || !onApplyAttributes) return;
                setApplyingPlayer(pi.playerId);
                try {
                  // Save previous attrs to localStorage for undo
                  localStorage.setItem(
                    `dugout_smartscout_undo_${pi.playerId}`,
                    JSON.stringify(pp.attributes)
                  );
                  // Merge only stats-derivable attributes
                  const merged: Record<string, Record<string, number>> = JSON.parse(JSON.stringify(pp.attributes));
                  const sa = pi.suggestedAttributes;
                  if (sa.hitting) {
                    if (!merged.hitting) merged.hitting = {};
                    if (sa.hitting.contact != null) merged.hitting.contact = sa.hitting.contact;
                    if (sa.hitting.power != null) merged.hitting.power = sa.hitting.power;
                    if (sa.hitting.eye != null) merged.hitting.eye = sa.hitting.eye;
                  }
                  if (sa.pitching) {
                    if (!merged.pitching) merged.pitching = {};
                    if (sa.pitching.velocity != null) merged.pitching.velocity = sa.pitching.velocity;
                    if (sa.pitching.control != null) merged.pitching.control = sa.pitching.control;
                  }
                  await onApplyAttributes(pi.playerId, merged);
                  setAppliedPlayers(prev => new Set([...prev, pi.playerId]));
                } catch { /* show nothing — error stays silent */ }
                finally { setApplyingPlayer(null); }
              }

              async function handleUndo(pi: PlayerInsight) {
                const raw = localStorage.getItem(`dugout_smartscout_undo_${pi.playerId}`);
                if (!raw || !onApplyAttributes) return;
                setApplyingPlayer(pi.playerId);
                try {
                  const prev = JSON.parse(raw);
                  await onApplyAttributes(pi.playerId, prev);
                  localStorage.removeItem(`dugout_smartscout_undo_${pi.playerId}`);
                  setAppliedPlayers(prev => { const s = new Set(prev); s.delete(pi.playerId); return s; });
                } catch { }
                finally { setApplyingPlayer(null); }
              }

              return (
                <section className="space-y-4">
                  <div className="flex items-center space-x-3 px-1">
                    <h3 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Coaching Mirror</h3>
                    <div className="flex-1 h-px bg-raised" />
                  </div>
                  <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest px-1">
                    Scout suggestion vs your rating · based on {payload.gameCount} games
                  </p>

                  {mirrorPlayers.map(pi => {
                    const pp = playerPayloadMap[pi.playerId];
                    const coachAttrs = pp?.attributes;
                    const sa = pi.suggestedAttributes!;
                    const isApplied = appliedPlayers.has(pi.playerId);
                    const isBusy = applyingPlayer === pi.playerId;

                    type AttrRow = { label: string; coach: number | null; scout: number };
                    const rows: AttrRow[] = [
                      ...(sa.hitting?.contact != null ? [{ label: 'Contact', coach: coachAttrs?.hitting?.contact ?? null, scout: sa.hitting.contact }] : []),
                      ...(sa.hitting?.power != null ? [{ label: 'Power', coach: coachAttrs?.hitting?.power ?? null, scout: sa.hitting.power }] : []),
                      ...(sa.hitting?.eye != null ? [{ label: 'Eye', coach: coachAttrs?.hitting?.eye ?? null, scout: sa.hitting.eye }] : []),
                      ...(sa.pitching?.velocity != null ? [{ label: 'Velocity', coach: coachAttrs?.pitching?.velocity ?? null, scout: sa.pitching.velocity }] : []),
                      ...(sa.pitching?.control != null ? [{ label: 'Control', coach: coachAttrs?.pitching?.control ?? null, scout: sa.pitching.control }] : []),
                    ];

                    const Dots = ({ val, color }: { val: number | null; color: string }) => (
                      <div className="flex space-x-0.5">
                        {[1,2,3,4,5].map(i => (
                          <div key={i} className={`w-2 h-2 rounded-full ${val != null && i <= val ? color : 'bg-raised'}`} />
                        ))}
                      </div>
                    );

                    return (
                      <div key={pi.playerId} className="bg-canvas rounded-2xl border border-hairline overflow-hidden">
                        <div className="px-5 pt-4 pb-3 border-b border-hairline flex items-center space-x-3">
                          <div className="w-7 h-7 bg-raised rounded-lg flex items-center justify-center">
                            <User className="w-3.5 h-3.5 text-ink-2" />
                          </div>
                          <p className="text-xs font-semibold uppercase tracking-wide">{pi.name}</p>
                        </div>
                        <div className="p-5 space-y-3">
                          {/* Column headers */}
                          <div className="grid grid-cols-[80px_1fr_1fr] gap-2">
                            <div />
                            <p className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest text-center">You</p>
                            <p className="text-[8px] font-semibold text-gold-ink/60 uppercase tracking-widest text-center">Scout</p>
                          </div>
                          {rows.map(row => {
                            const delta = row.coach != null ? row.scout - row.coach : 0;
                            const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : null;
                            const arrowColor = delta > 0 ? 'text-accent-ink' : 'text-danger';
                            return (
                              <div key={row.label} className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center">
                                <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">{row.label}</p>
                                <div className="flex flex-col items-center space-y-1">
                                  <Dots val={row.coach} color="bg-ink-2" />
                                  <span className="text-[9px] font-black text-ink-2">{row.coach ?? '—'}</span>
                                </div>
                                <div className="flex flex-col items-center space-y-1">
                                  <Dots val={row.scout} color="bg-gold" />
                                  <div className="flex items-center space-x-1">
                                    <span className="text-[9px] font-black text-gold-ink">{row.scout}</span>
                                    {arrow && <span className={`text-[9px] font-black ${arrowColor}`}>{arrow}</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {/* Apply / Undo */}
                          {onApplyAttributes && coachAttrs && (
                            <button
                              onClick={() => isApplied ? handleUndo(pi) : handleApply(pi)}
                              disabled={isBusy}
                              className={`mt-2 w-full py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all disabled:opacity-50 ${
                                isApplied
                                  ? 'bg-raised border border-ink-mute text-ink-2 hover:border-ink-mute'
                                  : 'bg-gold/15 border border-hairline text-gold-ink hover:bg-gold/25'
                              }`}
                            >
                              {isBusy ? 'Saving…' : isApplied ? '↩ Undo' : 'Apply Scout Ratings'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-[8px] text-ink-2 text-center leading-relaxed px-4">
                    Undo is available until you close Smart Scout. Only contact, power, eye, velocity, and control are updated. All other ratings stay yours.
                  </p>
                </section>
              );
            })()}

            {/* Regenerate button — appears when there are unsaved notes */}
            {hasUnsavedNotes && (
              <button
                onClick={handleRegenerate}
                disabled={refining}
                className="w-full py-4 bg-gold text-surface font-black rounded-2xl uppercase italic flex items-center justify-center space-x-2 hover:bg-gold-ink disabled:opacity-50 transition-all"
              >
                {refining
                  ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Regenerating…</span></>
                  : <><RefreshCw className="w-4 h-4" /><span>Regenerate with Coach Notes</span></>
                }
              </button>
            )}

            {/* Disclaimer */}
            <p className="text-[9px] text-ink-2 text-center leading-relaxed px-4">
              Smart Scout observations are generated from your team's imported game data. They are coaching aids, not evaluations of player value or recommendations for roster decisions.
            </p>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-hairline bg-surface">
        <button
          onClick={onClose}
          className="w-full py-4 bg-ink text-surface font-black rounded-2xl uppercase italic flex items-center justify-center space-x-2 hover:scale-105 transition-transform"
        >
          <span>Back to Stats</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
};

// ── Extracted so the initial load and refine share the same fetch logic ───────
async function callAPI(
  payload: InsightsPayload,
  coachNotes: Record<string, string>,
  cancelled: boolean,
  setInsights: (d: InsightsResult) => void,
  setCoachNotes: (n: Record<string, string>) => void,
  setSavedNotes: (n: Record<string, string>) => void,
  setError: (e: string | null) => void,
  setLoading: (b: boolean) => void,
) {
  const { cacheKey, teamId, gameSetHash, ...apiPayload } = payload;
  try {
    const previousReport = await fetchPreviousReport(teamId, gameSetHash);
    // Shared heartbeat-streamed POST — native via CapacitorHttp (readTimeout 60s),
    // web via fetch. Parses the final JSON line; tags HTTP/{error} as __httpError.
    const data = await postStreamedJson(
      apiUrl('/api/ai-insights'),
      await authHeaders(),
      JSON.stringify({ ...apiPayload, coachNotes, previousReport }),
      'SmartScout',
    );
    if (data?.__httpError) throw new Error(data.error || 'Request failed');
    if (!isValid(data)) throw new Error('Incomplete response from server');

    const stamped: InsightsResult = { ...data, generatedAt: new Date().toISOString(), gameCount: payload.gameCount };
    const toStore: InsightsResult = Object.keys(coachNotes).length ? { ...stamped, coachNotes } : stamped;
    try {
      await setDoc(doc(db, `teams/${teamId}/insights/${gameSetHash}`), toStore);
    } catch { /* write failed — still show result */ }
    lsSave(cacheKey, toStore);

    if (!cancelled) {
      setInsights(toStore);
      setCoachNotes(coachNotes);
      setSavedNotes({ ...coachNotes });
    }
  } catch (e: any) {
    if (!cancelled) setError(e.message);
  } finally {
    if (!cancelled) setLoading(false);
  }
}
